import { jumpApex, MOVE } from "@eldrun/sim";
import { CAMERA_NEAR } from "../../game/camera-rig.ts";

/**
 * Sumiço perto da câmera (`USE_NEAR_FADE`), em profundidade no espaço do olho (m): some de todo até
 * `gone` e fica inteiro a partir de `solid`, em pontilhado ordenado (Bayer 4 × 4) no meio. A
 * profundidade é a mesma medida que o plano near corta, então 5 cm além dele (0,15 m) nada chega a
 * ser fatiado, em qualquer campo de visão ou proporção de tela. A partir do raio do jogador (0,35 m)
 * fica inteiro o que um colisor mantém fora do corpo: só some o que o olho atravessa (galhos e copas
 * no salto, copas baixas) ou o que passa do colisor (o tronco da Amoreira).
 */
export const NEAR_FADE = { gone: CAMERA_NEAR + 0.05, solid: MOVE.radius } as const;

/**
 * Piso da casca no sumiço dos recursos (`USE_NEAR_FADE_FLOOR`), em metros acima da base da instância:
 * o olho no alto do salto sem bônus mais o fim do sumiço (≈ 3,15 m), para nenhuma casca que esse olho
 * alcança em chão plano pontilhar. Abaixo, a folga do tronco (tools/assets) já mantém a casca a um
 * plano near do olho, e o tronco em que se encosta para cortar fica sólido. Acima, só o salto com
 * bônus chega, e os galhos somem. A copa não tem piso: folha não tem colisão nem folga, e o olho
 * entra numa copa baixa (a Pereira) andando.
 */
export const NEAR_FADE_FLOOR = MOVE.eyeHeight + jumpApex() + NEAR_FADE.solid;

/**
 * Vento a partir do qual o vértice é copa (F42): a casca vai até 0,15·h² e a folha começa em 0,5, e
 * nenhum triângulo das árvores mistura os dois (`models-output.test.ts`). Rochas e veios (vento 0)
 * contam como casca.
 */
export const NEAR_FADE_LEAF_WIND = 0.3;

/**
 * Regra do shader nos recursos (com o piso): quem pode sumir perto da câmera, pelo vento do vértice
 * e pela altura (m) acima da base da instância. Espelha o GLSL para os testes com o pacote real.
 */
export const nearFadeEligible = (wind: number, height: number): boolean => wind >= NEAR_FADE_LEAF_WIND || height >= NEAR_FADE_FLOOR;
